/**
 * Trustpilot Safe Fetch Shim (Story 23.3 AC #5 + Story 35.3 AC #1)
 *
 * Thin wrapper around the generic `safeFetch` that ensures BEFORE any
 * logging, error reporting, or Sentry breadcrumb, every literal secret
 * substring is replaced with a redacted form:
 *
 *  - `Authorization: Bearer <accessToken>` → `Authorization: Bearer [redacted-trustpilot-token]`
 *  - `Authorization: Basic <base64>` → `Authorization: Basic [redacted-trustpilot-basic-auth]`
 *  - `apikey: <apiKey>` → `apikey: [redacted-trustpilot-api-key]`
 *
 * Per Round 3 Red Team Attack #1 + #2: this shim is the LAST line of
 * defense against any decrypted plaintext leaking into logs. The
 * `sanitizeTrustpilotErrorMessage` helper from `./errors` does the actual
 * replacement work — this shim just plumbs the three decrypted secrets
 * through to the error path.
 *
 * # Story 35.3 — TWO base URLs go live for the FIRST time.
 *
 * Per AC #1, the two hosts (`api.trustpilot.com` + `invitations-api.trustpilot.com`)
 * have INDEPENDENT rate-limit budgets per Trustpilot's documented per-host
 * 833/5min ceiling. The `hostCounters` Map below tracks each host's
 * `requestsThisMinute` / `requestsThisHour` independently. A 429 on
 * `invitations-api.trustpilot.com` MUST NOT poison `api.trustpilot.com`'s
 * counter (Round 3 Red Team Attack rate-limit-cross-poisoning defense).
 *
 * Co-located unit tests in `safe-fetch.test.ts` assert:
 *  - Bearer token redacted in error message
 *  - Basic auth redacted
 *  - API Key + Secret redacted as plaintext pair
 *  - base64-encoded `apiKey:apiSecret` form redacted
 *  - URL-encoded variants redacted (3 — 1 each for access token, API
 *    Key, API Secret)
 *  - `apikey:` header value redacted
 *  - Story 35.3: per-host counter independence (a 429 on invitations
 *    host does NOT increment api host counter)
 *  - Story 35.3: exact-string host dispatch (URL like
 *    `api.trustpilot.example.com` does NOT match `api.trustpilot.com`)
 */
import type { AdapterResult } from "../adapter";
import { adapterErr } from "../adapter";
import { safeFetch } from "../safe-fetch";

import { sanitizeTrustpilotErrorMessage } from "./errors";

/**
 * Coerce a review `id` value to a string for dedup-key use. Round 2
 * fix R2-11 — single source of truth for the helper, eliminating the
 * Round 1 byte-duplicate in trustpilot.ts + api.ts.
 *
 * Returns null on null/undefined/empty-string/non-scalar; returns the
 * string form of finite numbers (Trustpilot shape drift may emit
 * numeric IDs per round 1 fix #23).
 */
export function coerceTrustpilotReviewId(id: unknown): string | null {
  if (id === null || id === undefined) return null;
  if (typeof id === "string") return id.length > 0 ? id : null;
  if (typeof id === "number" && Number.isFinite(id)) return String(id);
  return null;
}

export interface TrustpilotSafeFetchContext {
  readonly decryptedAccessToken?: string;
  readonly decryptedApiKey?: string;
  readonly decryptedApiSecret?: string;
}

/**
 * Per-host rate-limit counter slot. Story 35.3 AC #1 — each host's
 * counter is independent.
 */
export interface TrustpilotHostCounter {
  requestsThisMinute: number;
  requestsThisHour: number;
  windowStartMinMs: number;
  windowStartHourMs: number;
}

/**
 * Discriminator for the host the URL targets. Story 35.3 AC #1.
 * `null` = neither known Trustpilot host (defensive — caller should not
 * route non-Trustpilot URLs through `safeFetchTrustpilot`, but we don't
 * throw to keep the wrapper a pure pass-through).
 */
export type TrustpilotHost = "api" | "invitations" | null;

/**
 * Lazily-initialized per-host counter Map. Story 35.3 AC #1 — a 429 on
 * `invitations-api.trustpilot.com` MUST NOT increment the
 * `api.trustpilot.com` counter (or vice versa). The Map is module-local
 * (process-scoped) — Workers re-init it on cold start which is
 * acceptable because the Trustpilot per-host budget is a 5-minute
 * sliding window the cron scheduler already respects at a higher level.
 *
 * Exported for test access only — do NOT mutate from adapter code.
 */
export const hostCounters: Map<"api" | "invitations", TrustpilotHostCounter> =
  new Map();

/**
 * Reset both per-host counters. Test helper — do NOT call from adapter
 * code. Used by `safe-fetch.test.ts` to isolate per-test counter state.
 */
export function __resetTrustpilotHostCounters(): void {
  hostCounters.clear();
}

/**
 * Classify a URL's host as one of the two known Trustpilot hosts, or
 * `null` for anything else. Story 35.3 AC #1.
 *
 * **CRITICAL invariant** — uses `new URL(url).hostname === <literal>`
 * (exact-string match), NOT `url.includes(<literal>)`. The latter would
 * falsely match a URL like `https://api.trustpilot.example.com/v1/...`
 * (a typo or attacker-controlled host) as `"api"`, sending decrypted
 * Bearer tokens to a wrong host.
 *
 * Invalid URLs (e.g., `"not-a-url"`) return `null` defensively.
 */
export function classifyTrustpilotHost(url: string): TrustpilotHost {
  let hostname: string;
  try {
    hostname = new URL(url).hostname;
  } catch {
    return null;
  }
  if (hostname === "api.trustpilot.com") return "api";
  if (hostname === "invitations-api.trustpilot.com") return "invitations";
  return null;
}

/**
 * Story 35.3 AC #1 — sub-helper. Returns true when `url` targets the
 * Invitations API host (`invitations-api.trustpilot.com`). Exact-string
 * hostname match; defensive against typo-look-alike URLs.
 */
export function isTrustpilotInvitationsHost(url: string): boolean {
  return classifyTrustpilotHost(url) === "invitations";
}

/**
 * Story 35.3 AC #1 — symmetric helper for `api.trustpilot.com`. Exists
 * mainly to make per-host dispatch in adapter code self-documenting.
 */
export function isTrustpilotPrimaryHost(url: string): boolean {
  return classifyTrustpilotHost(url) === "api";
}

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;

/**
 * Increment the per-host counter for the URL's host. Story 35.3 AC #1.
 *
 * The counter rolls over when the minute / hour window advances. The
 * caller doesn't need to read the counter — the Trustpilot adapter
 * defers rate-limit enforcement to the queue-throttling layer at a
 * higher level. The counter exists primarily so a future operator
 * dashboard can attribute 429s to the correct host, and so a 429 on
 * one host's response code path doesn't accidentally back off the
 * other host (the counter independence invariant).
 *
 * Returns the resolved host classification ("api" / "invitations" /
 * null) so callers can branch on it.
 */
export function incrementTrustpilotHostCounter(
  url: string,
  now: number = Date.now()
): TrustpilotHost {
  const host = classifyTrustpilotHost(url);
  if (host === null) return null;
  const existing = hostCounters.get(host);
  if (!existing) {
    hostCounters.set(host, {
      requestsThisMinute: 1,
      requestsThisHour: 1,
      windowStartMinMs: now,
      windowStartHourMs: now,
    });
    return host;
  }
  // Minute-window rollover
  if (now - existing.windowStartMinMs >= MINUTE_MS) {
    existing.requestsThisMinute = 1;
    existing.windowStartMinMs = now;
  } else {
    existing.requestsThisMinute += 1;
  }
  // Hour-window rollover
  if (now - existing.windowStartHourMs >= HOUR_MS) {
    existing.requestsThisHour = 1;
    existing.windowStartHourMs = now;
  } else {
    existing.requestsThisHour += 1;
  }
  return host;
}

/**
 * Wraps `safeFetch` so any error message is run through
 * `sanitizeTrustpilotErrorMessage` BEFORE it propagates to the adapter's
 * error path. This makes the redaction unavoidable — adapter code that
 * forgets to call `sanitizeTrustpilotErrorMessage` manually still gets
 * safe error messages.
 *
 * Story 35.3 AC #1 — increments the per-host counter BEFORE issuing the
 * fetch. The counter is incremented EXACTLY ONCE per call regardless of
 * success/failure (consistent with how Trustpilot itself counts requests
 * — a 429 is still a counted request that contributed to the budget).
 *
 * **Why a separate shim and not just sanitization at the call site?**
 * Defense-in-depth. Three things can leak a secret into an error:
 *   1. The URL contains a query-param secret (`?apikey=...`)
 *   2. The headers contain `Authorization: Bearer ...`
 *   3. The provider echoes the secret back in an error body
 * The shim catches #3 unconditionally; the generic `sanitizeErrorMessage`
 * catches #1 and #2 via its base64 + Bearer regex. Layering both gives
 * us bounded leak risk even when one layer has a regex bug.
 */
export async function safeFetchTrustpilot(
  url: string,
  init: RequestInit,
  context: TrustpilotSafeFetchContext = {},
  timeoutMs?: number
): Promise<AdapterResult<Response>> {
  const result = await safeFetch(url, init, timeoutMs);
  // Round 2 fix R2-3 — increment the per-host counter ONLY when the
  // request actually reached Trustpilot (success OR known providerStatus
  // like 429/4xx/5xx). DNS / network / abort failures never hit the
  // server and MUST NOT pollute the per-host budget — they would
  // otherwise inflate counters and cause spurious backoff in higher
  // layers.
  if (result.success || result.error.providerStatus !== undefined) {
    // Story 35.3 AC #1 — per-host counter increment. Unknown hosts
    // (`classifyTrustpilotHost` returns null) are silently NOT counted
    // — they shouldn't be routed through this shim, but we defensively
    // don't throw to preserve back-compat.
    incrementTrustpilotHostCounter(url);
  }

  if (result.success) return result;

  // Re-sanitize the error message + providerMessage with the targeted
  // Trustpilot redactor — strictly stronger than safeFetch's generic
  // sanitizer because it knows the decrypted plaintext values to look for.
  return adapterErr({
    ...result.error,
    message: sanitizeTrustpilotErrorMessage(
      result.error.message,
      context.decryptedAccessToken,
      context.decryptedApiKey,
      context.decryptedApiSecret
    ),
    providerMessage: result.error.providerMessage
      ? sanitizeTrustpilotErrorMessage(
          result.error.providerMessage,
          context.decryptedAccessToken,
          context.decryptedApiKey,
          context.decryptedApiSecret
        )
      : undefined,
  });
}
